/**
 * Button — Reusable button component.
 * Covers every button variant used across the converted UI.
 *
 * Props:
 *   variant     'primary' | 'secondary' | 'outline' | 'outlineBlue' |
 *               'ghost' | 'ghostFilled' | 'ghostNavy' | 'danger' |
 *               'dangerSolid' | 'navy' | 'icon' | 'glass' | 'text'
 *   size        'sm' | 'md' | 'lg'          (default 'md')
 *   leftIcon    React element — icon on the left  e.g. <IconEdit size={14} />
 *   rightIcon   React element — icon on the right e.g. <IconArrowRight size={14} />
 *   loading     bool — shows spinner, disables interaction
 *   disabled    bool
 *   fullWidth   bool
 *   as          'button' | 'a' | any tag string  (default 'button')
 *   type        'button' | 'submit' | 'reset'    (default 'button')
 *   className   extra classes to merge
 *
 * Usage examples:
 *   <Button variant="primary" leftIcon={<IconEdit size={14} />}>Edit</Button>
 *   <Button variant="secondary" loading={saving}>Update</Button>
 *   <Button variant="outline">Skip</Button>
 *   <Button variant="ghost" leftIcon={<IconChevronLeft size={16} />}>Back</Button>
 *   <Button variant="ghostFilled">Keep Match</Button>
 *   <Button variant="danger" size="sm" leftIcon={<IconTrash size={13} />}>Remove</Button>
 *   <Button variant="dangerSolid" leftIcon={<IconSend size={15} />}>Submit</Button>
 *   <Button variant="icon" size="md" aria-label="Notifications"><IconBell size={18} /></Button>
 *   <Button variant="text">Mark all as read</Button>
 *   <Button variant="primary" fullWidth>Continue</Button>
 */

import { forwardRef } from "react";
import { IconLoader2 } from "@tabler/icons-react";
import styles from "./button.module.css";
import classes from "./button.module.css";

const SPINNER_SIZE = { sm: 13, md: 15, lg: 17 };

const Button = forwardRef(function Button(
  {
    children,
    variant = "primary",
    size = "md",
    leftIcon,
    rightIcon,
    loading = false,
    disabled = false,
    fullWidth = false,
    as: Tag = "button",
    type = "button",
    className = "",
    onClick,
    ...rest
  },
  ref,
) {
  const classes = [
    styles.btn,
    styles[variant] ?? styles.primary,
    styles[size] ?? styles.md,
    fullWidth ? styles.fullWidth : "",
    loading ? styles.btnLoading : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const isDisabled = disabled || loading;

  return (
    <Tag
      ref={ref}
      type={Tag === "button" ? type : undefined}
      className={classes}
      disabled={Tag === "button" ? isDisabled : undefined}
      aria-disabled={isDisabled || undefined}
      onClick={isDisabled ? undefined : onClick}
      {...rest}
    >
      {/* Loading overlay — sits on top, keeps button dimensions stable */}
      {loading && (
        <span className={styles.loadingOverlay} aria-hidden="true">
          <IconLoader2
            size={SPINNER_SIZE[size] ?? 15}
            className={styles.spinner}
          />
        </span>
      )}

      {/* Left icon */}
      {!loading && leftIcon && (
        <span className={styles.iconSlot} aria-hidden="true">
          {leftIcon}
        </span>
      )}

      {/* Label text — hidden (but present) while loading so width stays stable */}
      {children && <span className={styles.label}>{children}</span>}

      {/* Right icon */}
      {!loading && rightIcon && (
        <span className={styles.iconSlot} aria-hidden="true">
          {rightIcon}
        </span>
      )}
    </Tag>
  );
});

export default Button;

export const YellowButton = ({
  onClick,
  children,
  extra,
  disabled = false,
  type = "button",
  className = "",
}) => {
  return (
    <button
      type={type}
      className={`${extra} ${classes.yellow} ${classes.button} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export const WhiteButton = ({
  onClick,
  children,
  extra,
  type = "button",
  className = "",
  disabled = false,
}) => {
  return (
    <button
      type={type}
      className={`${extra} ${classes.white} ${classes.button} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export const BlueButton = ({
  onClick,
  children,
  extra,
  type = "button",
  className = "",
  disabled = false,
}) => {
  return (
    <button
      type={type}
      className={`${extra} ${classes.blue} ${classes.button} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export const SettingYellowBtn = ({
  onClick,
  children,
  extra,
  disabled = false,
  type = "button",
}) => {
  return (
    <button
      type={type}
      className={`${extra} ${classes.settingYellow} ${classes.button}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};
